import React, { useEffect, useState } from 'react';
import { Alert, Button, Flex, Notification, Paper, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import { IconCalendar, IconInfoCircle } from '@tabler/icons-react';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useGetLifeEventById } from '../../../global/entities/lifeEvents/queries/useGetLifeEventById.tsx';
import { usePostLifeEventMutations } from '../../../global/entities/lifeEvents/mutations/usePostLifeEventMutations.tsx';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { useGetLifeEvents } from '../../../global/entities/lifeEvents/queries/useGetLifeEvents.tsx';
import { usePutLifeEventMutations } from '../../../global/entities/lifeEvents/mutations/usePutLifeEventMutations.tsx';
import { useParams } from 'react-router';
import { ILifeEvent } from '../../../global/entities/lifeEvents/LifeEvent.ts';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { useNavigate } from 'react-router-dom';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { RouteStore } from '../../../global/Store/RouteStore.ts';

export type ILifeEventsFormProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const LifeEventsForm: React.FC<ILifeEventsFormProps> = inject(
    'patientStore',
    'routeStore',
)(
    observer(({ patientStore, routeStore }) => {
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();
        const { t } = useTranslation();

        const params = useParams<{ patientId: string; tabId: string; id: string }>();

        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);

        const { data: lifeEvent } = useGetLifeEventById();
        const { refetch: refetchLifeEvents } = useGetLifeEvents(patientStore?.patientId || currentUser?.patientID);
        const postLifeEvent = usePostLifeEventMutations();
        const putLifeEvent = usePutLifeEventMutations();

        const schema = z.object({
            date: z.date(),
            title: z.string().min(1, { message: t('formError.titleIsRequired') }),
            description: z.string(),
        });

        const form = useForm({
            mode: 'controlled',
            initialValues: {
                id: Number(params.id),
                patientID: Number(params.patientId) || currentUser?.patientID,
                date: new Date(),
                title: '',
                description: '',
            },
            validate: zodResolver(schema),
        });

        const handleSubmit = async (values: ILifeEvent) => {
            setError(null);
            setIsSubmitting(true);

            const patientID: string | undefined = patientStore?.patientId || currentUser?.patientID;

            try {
                const payload = {
                    ...values,
                    patientID,
                };

                if (lifeEvent && patientID) {
                    await putLifeEvent.mutateAsync({
                        id: lifeEvent.id.toString(),
                        lifeEvent: payload as unknown as ILifeEvent,
                    });
                } else 
                    await postLifeEvent.mutateAsync(payload as unknown as ILifeEvent);
                
            } catch (e) {
                if (e instanceof AppMedError) 
                    setError(e.message);
                 else 
                    setError('An unexpected error occurred.');
                
            } finally {
                await refetchLifeEvents();
                setIsSubmitting(false);
                navigate(routeStore?.lastRoute || '..');
            }
        };

        useEffect(() => {
            if (!lifeEvent) return;
            form.setValues(lifeEvent);
        }, [lifeEvent]);

        return (
            <Paper h={'min-content'} w={'100%'}>
                <form
                    onSubmit={form.onSubmit((values) => {
                        handleSubmit(values as unknown as ILifeEvent);
                    })}
                >
                    <Stack w={'100%'} gap={'md'}>
                        <TextInput
                            label={t('patientLifeEvent.addLifeEvent')}
                            placeholder={t('patientLifeEvent.title')}
                            {...form.getInputProps('title')}
                            withAsterisk
                        />
                        <Textarea
                            label={t('patientLifeEvent.description')}
                            placeholder={t('patientLifeEvent.descriptionPlaceHolder')}
                            autosize
                            minRows={6}
                            maxRows={12}
                            {...form.getInputProps('description')}
                        />
                        <CalendarDateInput
                            label={t('formGlobal.date')}
                            placeholder={t('placeHolder.dateFormat')}
                            leftSection={<IconCalendar size={16} />}
                            value={lifeEvent?.date}
                            {...form.getInputProps('date')}
                        />
                        <Alert icon={<IconInfoCircle />} mt={'md'} mb={'md'} variant={'light'} color={'yellow'}>
                            {t('patientLifeEvent.alert')}
                        </Alert>
                        <Flex gap={'sm'} justify={'flex-end'} w={'100%'}>
                            <Button
                                disabled={isSubmitting}
                                type={'button'}
                                onClick={() => navigate(routeStore?.lastRoute || '..')}
                                variant="default"
                            >
                                {t('button.cancel')}
                            </Button>
                            <Button disabled={isSubmitting} type={'submit'}>
                                {t('button.save')}
                            </Button>
                        </Flex>
                    </Stack>
                    {error && (
                        <Notification mt={'md'} color="red" onClose={() => setError(null)}>
                            {error}
                        </Notification>
                    )}
                </form>
            </Paper>
        );
    }),
);

export default LifeEventsForm;
